import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { encode } from "base64-arraybuffer";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import moment from "moment";

function AntiguedadSaldosVencido() {
  const { user } = useContext(AuthContext);

  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const URL_SEND_MAIL_ATT = process.env.REACT_APP_URL_SEND_MAIL_ATT;

  const money = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(v || 0));

  const monthLabel = (ym) => {
    const m = moment(ym + "-01");
    return m.locale("es").format("MMMM YYYY").replace(/^\w/, (c) => c.toUpperCase());
  };

  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");

  const [modalMail, setModalMail] = useState(false);
  const toggleMail = () => setModalMail(!modalMail);
  const [mailTo, setMailTo] = useState("");

  const [comments, setComments] = useState([]);
  const [meses, setMeses] = useState([]);

  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

  const [totalSaldoGeneral, setTotalSaldoGeneral] = useState(0);
  const [totalEnTiempoGeneral, setTotalEnTiempoGeneral] = useState(0);
  const [totalVencidoGeneral, setTotalVencidoGeneral] = useState(0);
  const [totalesMesGeneral, setTotalesMesGeneral] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      try {
        showLoader();

        const [respDash, respCli] = await Promise.all([
          axios.get(`${URL_DASHBOARDS}AntiguedadSaldosVencido`, {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          }),
          axios.get(URL_CLIENTES, {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          }),
        ]);

        const payload = respDash?.data || {};
        const mesesAPI = Array.isArray(payload.meses) ? payload.meses : [];
        const dataAPI = Array.isArray(payload.data) ? payload.data : [];

        const rows = dataAPI
          .slice()
          .sort((a, b) =>
            (a?.nombreCliente || "") > (b?.nombreCliente || "") ? 1 : -1
          )
          .map((a) => ({
            idCliente: a?.clienteId,
            cliente: a?.nombreCliente || "",
            saldo: Number(a?.saldoTotal || 0),
            enTiempo: Number(a?.enTiempo || 0),
            vencido: Number(a?.vencidoTotal || 0),
            meses: a?.meses || {},
          }));

        const mesesInferidos =
          mesesAPI.length > 0
            ? mesesAPI
            : Array.from(
                rows.reduce((set, r) => {
                  Object.keys(r.meses || {}).forEach((k) => set.add(k));
                  return set;
                }, new Set())
              ).sort((a, b) => (a > b ? 1 : -1));

        setMeses(mesesInferidos);
        setComments(rows);
        setClientes(respCli?.data || []);
      } catch (err) {
        console.log(err);
      } finally {
        hideLoader();
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headers = useMemo(() => {
    const base = [
      { name: "Cliente", field: "cliente", sortable: true },
      { name: "Saldo Total", field: "saldo", sortable: true },
      { name: "En Tiempo", field: "enTiempo", sortable: true },
      { name: "Vencido", field: "vencido", sortable: true },
    ];

    const mesesCols = (meses || []).map((ym) => ({
      name: monthLabel(ym),
      field: `mes__${ym}`,
      sortable: true,
    }));

    return [...base, ...mesesCols];
  }, [meses]);

  const commentsData = useMemo(() => {
    let computed = comments;

    if (search) {
      const s = search.toLowerCase();

      computed = computed.filter((r) => {
        if ((r.cliente || "").toLowerCase().includes(s)) return true;
        if (String(r.saldo || "").includes(search)) return true;
        if (String(r.enTiempo || "").includes(search)) return true;
        if (String(r.vencido || "").includes(search)) return true;

        const m = r.meses || {};
        return Object.keys(m).some((k) => String(m[k] || "").includes(search));
      });
    }

    if (selectedCliente) {
      computed = computed.filter(
        (r) => String(r.idCliente) === String(selectedCliente)
      );
    }

    setTotalItems(computed.length);

    const totalSaldo = computed.reduce(
      (acc, r) => acc + Number(r.saldo || 0),
      0
    );

    const totalEnTiempo = computed.reduce(
      (acc, r) => acc + Number(r.enTiempo || 0),
      0
    );

    const totalVencido = computed.reduce(
      (acc, r) => acc + Number(r.vencido || 0),
      0
    );

    const totMes = {};
    (meses || []).forEach((ym) => {
      totMes[ym] = computed.reduce(
        (acc, r) => acc + Number((r.meses || {})[ym] || 0),
        0
      );
    });

    setTotalSaldoGeneral(totalSaldo);
    setTotalEnTiempoGeneral(totalEnTiempo);
    setTotalVencidoGeneral(totalVencido);
    setTotalesMesGeneral(totMes);

    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;

      if (sorting.field === "cliente") {
        computed = computed
          .slice()
          .sort(
            (a, b) =>
              reversed * (a.cliente || "").localeCompare(b.cliente || "")
          );
      }

      if (
        sorting.field === "saldo" ||
        sorting.field === "enTiempo" ||
        sorting.field === "vencido"
      ) {
        computed = computed
          .slice()
          .sort(
            (a, b) =>
              reversed *
              (Number(a[sorting.field] || 0) -
                Number(b[sorting.field] || 0))
          );
      }

      if (String(sorting.field).startsWith("mes__")) {
        const ym = String(sorting.field).replace("mes__", "");

        computed = computed.slice().sort((a, b) => {
          const av = Number((a.meses || {})[ym] || 0);
          const bv = Number((b.meses || {})[ym] || 0);
          return reversed * (av - bv);
        });
      }
    }

    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting, selectedCliente, meses]);

  function buildRowsForExport() {
    let data = comments;

    if (search) {
      const s = search.toLowerCase();

      data = data.filter((r) => {
        if ((r.cliente || "").toLowerCase().includes(s)) return true;
        if (String(r.saldo || "").includes(search)) return true;
        if (String(r.enTiempo || "").includes(search)) return true;
        if (String(r.vencido || "").includes(search)) return true;

        const m = r.meses || {};
        return Object.keys(m).some((k) => String(m[k] || "").includes(search));
      });
    }

    if (selectedCliente) {
      data = data.filter((r) => String(r.idCliente) === String(selectedCliente));
    }

    return data;
  }

  function PDFTabla() {
    const rows = buildRowsForExport();

    const head = [
      [
        "Cliente",
        "Saldo Total",
        "En Tiempo",
        "Vencido",
        ...(meses || []).map((ym) => monthLabel(ym)),
      ],
    ];

    const body = rows.map((r) => [
      r.cliente,
      money(r.saldo),
      money(r.enTiempo),
      money(r.vencido),
      ...(meses || []).map((ym) => money((r.meses || {})[ym] || 0)),
    ]);

    const doc = new jsPDF("landscape");

    const img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 30, 45, 15);

    const img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 140, 15, 25, 25);

    doc.text("Antigüedad de Saldos Vencido por Mes", 20, 30);

    doc.autoTable({
      head,
      body,
      startY: 45,
      styles: { fontSize: 8 },
      foot: [
        [
          "TOTAL",
          money(totalSaldoGeneral),
          money(totalEnTiempoGeneral),
          money(totalVencidoGeneral),
          ...(meses || []).map((ym) =>
            money((totalesMesGeneral || {})[ym] || 0)
          ),
        ],
      ],
      showFoot: "lastPage",
    });

    window.open(doc.output("bloburl"), "_blank");
  }

  function excel() {
    const rows = buildRowsForExport();

    const sheetData = rows.map((r) => {
      const obj = {
        Cliente: r.cliente,
        "Saldo Total": money(r.saldo),
        "En Tiempo": money(r.enTiempo),
        Vencido: money(r.vencido),
      };

      (meses || []).forEach((ym) => {
        obj[monthLabel(ym)] = money((r.meses || {})[ym] || 0);
      });

      return obj;
    });

    const cols = [
      "Cliente",
      "Saldo Total",
      "En Tiempo",
      "Vencido",
      ...(meses || []).map((ym) => monthLabel(ym)),
    ];

    const option = {
      fileName: "AntiguedadSaldosVencido_PorMes",
      datas: [
        {
          sheetData,
          sheetName: "VencidoPorMes",
          sheetFilter: cols,
          sheetHeader: cols,
        },
      ],
    };

    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  function enviaMail() {
    const rows = buildRowsForExport();

    const head = [
      [
        "Cliente",
        "Saldo Total",
        "En Tiempo",
        "Vencido",
        ...(meses || []).map((ym) => monthLabel(ym)),
      ],
    ];

    const body = rows.map((r) => [
      r.cliente,
      money(r.saldo),
      money(r.enTiempo),
      money(r.vencido),
      ...(meses || []).map((ym) => money((r.meses || {})[ym] || 0)),
    ]);

    const doc = new jsPDF("landscape");

    const img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 30, 45, 15);

    const img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 140, 15, 25, 25);

    doc.text("Antigüedad de Saldos Vencido por Mes", 20, 30);

    doc.autoTable({
      head,
      body,
      startY: 45,
      styles: { fontSize: 8 },
      foot: [
        [
          "TOTAL",
          money(totalSaldoGeneral),
          money(totalEnTiempoGeneral),
          money(totalVencidoGeneral),
          ...(meses || []).map((ym) =>
            money((totalesMesGeneral || {})[ym] || 0)
          ),
        ],
      ],
      showFoot: "lastPage",
    });

    const att = doc.output("arraybuffer");
    const base64File = encode(att);

    axios
      .post(
        URL_SEND_MAIL_ATT,
        {
          subject: "Antigüedad Saldos Vencido por Mes",
          email: mailTo,
          fileName: "AntiguedadSaldosVencido_PorMes.pdf",
          att: base64File,
          text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
            <th><img width="${process.env.REACT_APP_TAMANO_LOGO_MAIL}" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}logoInstitucion.png" /></th>
            <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${process.env.REACT_APP_NOMBRE_INSTITUCION}</tr>
            <tbody>
            <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
            <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Adjunto encontrarás el reporte de Antigüedad de Saldos Vencido por Mes.</tr>
            </tbody>
            <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda favor de comunicarse al ${process.env.REACT_APP_TELEFONO_INSTITUCION}</tr>
            <tr align="right"><br>Powered by <a href="https://lynqro.com"> <img width="20%" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}Logo-Lynqro-PDF.png" /> </a> </tr>
          </table>`,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then(() => {
        toggleMail();
        Swal.fire({
          position: "center",
          icon: "success",
          title: "Enviado con Exito!",
          showConfirmButton: false,
          timer: 2000,
        });
        setMailTo("");
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          footer: `${error?.response?.data || error?.message}`,
        });
        console.log(error);
      });
  }

  let totalSaldo = 0;
  let totalEnTiempo = 0;
  let totalVencido = 0;
  const totalMes = {};

  (meses || []).forEach((ym) => {
    totalMes[ym] = 0;
  });

  return (
    <>
      <Header />
      <br />
      <br />

      {user.menu_admin ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuAdmin"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>

            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button
                  size="sm"
                  className="btn"
                  color="danger"
                  onClick={PDFTabla}
                >
                  PDF <i className="far fa-file-pdf"></i>
                </Button>

                <Button
                  size="sm"
                  className="btn"
                  onClick={toggleMail}
                  color="info"
                >
                  eMail <i className="fas fa-at"></i>
                </Button>

                <Button
                  size="sm"
                  className="btn"
                  color="primary"
                  onClick={excel}
                >
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Antigüedad de Saldos Vencido por Mes</h3>

          <Row className="mb-2">
            <Col md={6}>
              <Label>Cliente</Label>
              <Input
                type="select"
                bsSize="sm"
                value={selectedCliente}
                onChange={(e) => {
                  setSelectedCliente(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                {clientes
                  .slice()
                  .sort((a, b) =>
                    (a?.nombre_comercial || "") >
                    (b?.nombre_comercial || "")
                      ? 1
                      : -1
                  )
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nombre_comercial}
                    </option>
                  ))}
              </Input>
            </Col>

            <Col md={6} className="d-flex flex-row-reverse align-items-end">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </Col>
          </Row>

          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          <br />

          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />

            <tbody>
              {commentsData.map((a) => {
                totalSaldo += Number(a.saldo || 0);
                totalEnTiempo += Number(a.enTiempo || 0);
                totalVencido += Number(a.vencido || 0);

                (meses || []).forEach((ym) => {
                  totalMes[ym] =
                    (totalMes[ym] || 0) + Number((a.meses || {})[ym] || 0);
                });

                return (
                  <tr key={a.idCliente}>
                    <td>{a.cliente}</td>
                    <td>{money(a.saldo)}</td>
                    <td>{money(a.enTiempo)}</td>
                    <td>{money(a.vencido)}</td>

                    {(meses || []).map((ym) => (
                      <td key={ym}>{money((a.meses || {})[ym] || 0)}</td>
                    ))}
                  </tr>
                );
              })}

              <tr>
                <td className="negrita" align="center">
                  TOTAL
                </td>
                <td className="negrita">{money(totalSaldo)}</td>
                <td className="negrita">{money(totalEnTiempo)}</td>
                <td className="negrita">{money(totalVencido)}</td>

                {(meses || []).map((ym) => (
                  <td key={ym} className="negrita">
                    {money(totalMes[ym] || 0)}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="negrita" align="center">
                  TOTAL GENERAL
                </td>
                <td className="negrita">{money(totalSaldoGeneral)}</td>
                <td className="negrita">{money(totalEnTiempoGeneral)}</td>
                <td className="negrita">{money(totalVencidoGeneral)}</td>

                {(meses || []).map((ym) => (
                  <td key={ym} className="negrita">
                    {money((totalesMesGeneral || {})[ym] || 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>

          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}

      <br />

      <Modal size="sm" isOpen={modalMail} toggle={toggleMail}>
        <ModalHeader toggle={toggleMail}>
          <h4>Enviar Antigüedad Saldos Vencido por Mes</h4>
        </ModalHeader>

        <ModalBody>
          <Label className="mr-sm-2">Email</Label>
          <Input
            className="col-sm-12"
            type="text"
            value={mailTo}
            required
            onChange={(e) => setMailTo(e.target.value)}
          />
        </ModalBody>

        <ModalFooter>
          <Button size="sm" color="success" onClick={enviaMail}>
            Enviar
          </Button>
        </ModalFooter>
      </Modal>

      {loader}
    </>
  );
}

export default AntiguedadSaldosVencido;